import {
  normalisePagedResult,
  type AuditLogEntry,
  type AuthUser,
  type BulkInviteResult,
  type CreateInviteRequest,
  type Invite,
  type PagedResult,
  type RawPagedResult,
  type Role,
  type UserStatus,
} from "@ir/types";
import { adminApi, request } from "./http";

/* -------------------------------------------------------------------------- */
/* Users                                                                       */
/* -------------------------------------------------------------------------- */

export interface UserQuery {
  searchTerm?: string;
  role?: Role | "";
  status?: UserStatus | "";
  pageNumber?: number;
  pageSize?: number;
  sortBy?: "email" | "name" | "role" | "status" | "last_login_at" | "created_at";
  sortDirection?: "asc" | "desc";
  signal?: AbortSignal;
}

export async function listUsers({
  searchTerm = "",
  role = "",
  status = "",
  pageNumber = 1,
  pageSize = 25,
  sortBy = "created_at",
  sortDirection = "desc",
  signal,
}: UserQuery = {}): Promise<PagedResult<AuthUser>> {
  const response = await request(
    () =>
      adminApi.get<RawPagedResult<AuthUser>>("/manage/users", {
        params: { searchTerm, role, status, pageNumber, pageSize, sortBy, sortDirection },
        signal,
      }),
    "Failed to load users.",
  );
  return normalisePagedResult(response.data, pageNumber, pageSize);
}

export async function updateUserRole(userId: string, role: Role): Promise<AuthUser> {
  const response = await request(
    () => adminApi.patch<AuthUser>(`/manage/users/${encodeURIComponent(userId)}/role`, { role }),
    "Failed to update that user's role.",
  );
  return response.data;
}

export async function updateUserStatus(
  userId: string,
  status: Extract<UserStatus, "active" | "disabled">,
): Promise<AuthUser> {
  const response = await request(
    () =>
      adminApi.patch<AuthUser>(`/manage/users/${encodeURIComponent(userId)}/status`, {
        status,
      }),
    "Failed to update that user's access.",
  );
  return response.data;
}

export async function updateUserDeveloperTools(
  userId: string,
  developerToolsEnabled: boolean,
): Promise<AuthUser> {
  const response = await request(
    () =>
      adminApi.patch<AuthUser>(
        `/manage/users/${encodeURIComponent(userId)}/developer-tools`,
        { developer_tools_enabled: developerToolsEnabled },
      ),
    "Failed to update that user's developer tools access.",
  );
  return response.data;
}

/* -------------------------------------------------------------------------- */
/* Invites                                                                     */
/* -------------------------------------------------------------------------- */

export interface InviteQuery {
  searchTerm?: string;
  status?: "pending" | "accepted" | "revoked" | "expired" | "";
  pageNumber?: number;
  pageSize?: number;
  sortBy?: "email" | "role" | "expires_at" | "created_at";
  sortDirection?: "asc" | "desc";
  signal?: AbortSignal;
}

export async function listInvites({
  searchTerm = "",
  status = "",
  pageNumber = 1,
  pageSize = 25,
  sortBy = "created_at",
  sortDirection = "desc",
  signal,
}: InviteQuery = {}): Promise<PagedResult<Invite>> {
  const response = await request(
    () =>
      adminApi.get<RawPagedResult<Invite>>("/manage/invites", {
        params: { searchTerm, status, pageNumber, pageSize, sortBy, sortDirection },
        signal,
      }),
    "Failed to load invites.",
  );
  return normalisePagedResult(response.data, pageNumber, pageSize);
}

export async function createInvite(payload: CreateInviteRequest): Promise<Invite> {
  const response = await request(
    () => adminApi.post<Invite>("/manage/invites", payload),
    "Failed to send that invite.",
  );
  return response.data;
}

export async function createInvitesBulk(
  invites: CreateInviteRequest[],
): Promise<BulkInviteResult[]> {
  const response = await request(
    () => adminApi.post<BulkInviteResult[]>("/manage/invites/bulk", { invites }),
    "Failed to send the bulk invites.",
  );
  return response.data ?? [];
}

export async function revokeInvite(inviteId: string): Promise<void> {
  await request(
    () => adminApi.post(`/manage/invites/${encodeURIComponent(inviteId)}/revoke`, {}),
    "Failed to revoke that invite.",
  );
}

export async function resendInvite(inviteId: string): Promise<Invite> {
  const response = await request(
    () => adminApi.post<Invite>(`/manage/invites/${encodeURIComponent(inviteId)}/resend`, {}),
    "Failed to resend that invite.",
  );
  return response.data;
}

/** Column order the bulk-invite parser expects. */
export const BULK_INVITE_TEMPLATE = "email,role\nsomeone@insurancerevolution.co.uk,user\n";

export interface ParsedBulkInvite extends CreateInviteRequest {
  line: number;
}

export interface BulkInviteParseResult {
  invites: ParsedBulkInvite[];
  errors: { line: number; message: string }[];
}

const VALID_ROLES: Role[] = ["admin", "developer", "user", "read_only"];

/**
 * Parses the bulk-invite CSV. Kept client-side so the admin sees bad rows
 * before anything is sent; the server validates again regardless.
 */
export function parseBulkInviteCsv(csv: string): BulkInviteParseResult {
  const invites: ParsedBulkInvite[] = [];
  const errors: { line: number; message: string }[] = [];
  const seen = new Set<string>();

  const lines = csv.split(/\r?\n/);

  lines.forEach((rawLine, index) => {
    const line = rawLine.trim();
    const lineNumber = index + 1;
    if (!line) return;
    if (index === 0 && /^email\s*,/i.test(line)) return; // header

    const [rawEmail, rawRole = "user"] = line.split(",").map((part) => part.trim());
    const email = rawEmail?.toLowerCase() ?? "";

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.push({ line: lineNumber, message: `"${rawEmail ?? ""}" is not a valid email address.` });
      return;
    }
    if (seen.has(email)) {
      errors.push({ line: lineNumber, message: `${email} appears more than once.` });
      return;
    }

    const role = rawRole.toLowerCase() as Role;
    if (!VALID_ROLES.includes(role)) {
      errors.push({
        line: lineNumber,
        message: `"${rawRole}" is not a role. Use admin, developer, user or read_only.`,
      });
      return;
    }

    seen.add(email);
    invites.push({ email, role, line: lineNumber });
  });

  return { invites, errors };
}

/* -------------------------------------------------------------------------- */
/* Audit                                                                       */
/* -------------------------------------------------------------------------- */

export interface AuditQuery {
  searchTerm?: string;
  action?: string;
  actorEmail?: string;
  from?: string;
  to?: string;
  pageNumber?: number;
  pageSize?: number;
  sortBy?: "created_at" | "actor_email" | "action" | "resource_type" | "resource_id";
  sortDirection?: "asc" | "desc";
  signal?: AbortSignal;
}

export async function listAuditLog({
  searchTerm = "",
  action = "",
  actorEmail = "",
  from = "",
  to = "",
  pageNumber = 1,
  pageSize = 50,
  sortBy = "created_at",
  sortDirection = "desc",
  signal,
}: AuditQuery = {}): Promise<PagedResult<AuditLogEntry>> {
  const response = await request(
    () =>
      adminApi.get<RawPagedResult<AuditLogEntry>>("/manage/audit", {
        params: {
          searchTerm,
          action,
          actorEmail,
          from,
          to,
          pageNumber,
          pageSize,
          sortBy,
          sortDirection,
        },
        signal,
      }),
    "Failed to load the audit log.",
  );
  return normalisePagedResult(response.data, pageNumber, pageSize);
}

/* -------------------------------------------------------------------------- */
/* Export                                                                      */
/* -------------------------------------------------------------------------- */

function escapeCsvCell(value: unknown): string {
  const text = value === null || value === undefined ? "" : String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export function toCsv<T extends Record<string, unknown>>(
  rows: T[],
  columns: { key: keyof T & string; header: string }[],
): string {
  const header = columns.map((column) => escapeCsvCell(column.header)).join(",");
  const body = rows
    .map((row) => columns.map((column) => escapeCsvCell(row[column.key])).join(","))
    .join("\n");
  return `${header}\n${body}\n`;
}

export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
